import { computed, ref, type ComputedRef, type Ref } from 'vue'

import {
  ASIDE_WIDTH_DEFAULT_RATIO,
  ASIDE_WIDTH_MAX_PX,
  ASIDE_WIDTH_MIN_PX,
} from './constants'

const getViewportWidth = () =>
  (typeof document !== 'undefined' && document.documentElement.clientWidth)
  || (typeof window !== 'undefined' ? window.innerWidth : ASIDE_WIDTH_MAX_PX)

export const clampAsideWidth = (value: number) =>
  Math.min(ASIDE_WIDTH_MAX_PX, Math.max(ASIDE_WIDTH_MIN_PX, Math.round(value)))

export const getDefaultAsideWidth = (viewportWidth = getViewportWidth()) =>
  clampAsideWidth(viewportWidth * ASIDE_WIDTH_DEFAULT_RATIO)

export type AsideWidthPolicy = {
  storageKey: string
  widthPx: Ref<number>
  manualWidthPx: Ref<number | null>
  isManualWidth: ComputedRef<boolean>
  asideWidthStyle: ComputedRef<Record<string, string>>
  setManualWidth: (value: number) => void
  previewWidth: (value: number) => void
  resetToDefault: () => void
  syncWidthFromPolicy: () => void
  attachListeners: () => void
  detachListeners: () => void
  minWidthPx: number
  maxWidthPx: number
}

const readStoredManualWidth = (storageKey: string): number | null => {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw === null) return null
    const parsed = Number.parseFloat(raw)
    if (!Number.isFinite(parsed)) return null
    return clampAsideWidth(parsed)
  } catch {
    return null
  }
}

const persistManualWidth = (storageKey: string, value: number | null) => {
  if (typeof localStorage === 'undefined') return
  try {
    if (value === null) localStorage.removeItem(storageKey)
    else localStorage.setItem(storageKey, String(value))
  } catch (error) {
    console.error('Failed to persist aside width', error)
  }
}

export const createAsideWidthPolicy = (storageKey: string): AsideWidthPolicy => {
  const manualWidthPx = ref<number | null>(readStoredManualWidth(storageKey))
  const widthPx = ref(manualWidthPx.value ?? getDefaultAsideWidth())

  const syncWidthFromPolicy = () => {
    widthPx.value = manualWidthPx.value ?? getDefaultAsideWidth()
  }

  const onWindowResize = () => {
    if (manualWidthPx.value !== null) return
    widthPx.value = getDefaultAsideWidth()
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== storageKey) return
    if (event.newValue === null) {
      manualWidthPx.value = null
      widthPx.value = getDefaultAsideWidth()
      return
    }
    const parsed = Number.parseFloat(event.newValue)
    if (!Number.isFinite(parsed)) return
    const next = clampAsideWidth(parsed)
    manualWidthPx.value = next
    widthPx.value = next
  }

  let listenerCount = 0

  const attachListeners = () => {
    if (typeof window === 'undefined') return
    if (listenerCount === 0) {
      window.addEventListener('resize', onWindowResize)
      window.addEventListener('storage', onStorage)
    }
    listenerCount += 1
  }

  const detachListeners = () => {
    if (typeof window === 'undefined') return
    listenerCount = Math.max(0, listenerCount - 1)
    if (listenerCount === 0) {
      window.removeEventListener('resize', onWindowResize)
      window.removeEventListener('storage', onStorage)
    }
  }

  const setManualWidth = (value: number) => {
    const next = clampAsideWidth(value)
    manualWidthPx.value = next
    widthPx.value = next
    persistManualWidth(storageKey, next)
  }

  const resetToDefault = () => {
    manualWidthPx.value = null
    persistManualWidth(storageKey, null)
    widthPx.value = getDefaultAsideWidth()
  }

  const previewWidth = (value: number) => {
    widthPx.value = clampAsideWidth(value)
  }

  return {
    storageKey,
    widthPx,
    manualWidthPx,
    isManualWidth: computed(() => manualWidthPx.value !== null),
    asideWidthStyle: computed((): Record<string, string> => ({
      width: `${widthPx.value}px`,
    })),
    setManualWidth,
    previewWidth,
    resetToDefault,
    syncWidthFromPolicy,
    attachListeners,
    detachListeners,
    minWidthPx: ASIDE_WIDTH_MIN_PX,
    maxWidthPx: ASIDE_WIDTH_MAX_PX,
  }
}
